import React, {useState, useRef, useEffect, useCallback} from 'react';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components';
import {Modal, Icon, FlexRow} from '@forecast-it/design-system';
import {fetchQuery, graphql} from 'relay-runtime';
import environment from '../../../RelayEnvironment';
import type {AiProjectPlanningModal_ApiKey_Query$data} from './__generated__/AiProjectPlanningModal_ApiKey_Query.graphql';
import type {
	AiProjectPlanningModalProps,
	AttachedFile,
	ChatMessage,
	Phase,
	ProjectProposal,
	StreamCallbacks,
	Warning,
} from './types';
import {SimpleUUID} from './constants';
import {invokeAgent, resumeAgent} from './agentApi';
import {Body, Divider} from './styles';
import ChatPanel from './ChatPanel';
import ProposalPanel from './ProposalPanel';

const apiKeyQuery = graphql`
	query AiProjectPlanningModal_ApiKey_Query {
		viewer {
			company {
				apiKeys(first: 1) {
					edges {
						node {
							key
							active
						}
					}
				}
			}
		}
	}
`;

const TitleIconBadge = Styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: linear-gradient(135deg, #6e0fea 0%, #392664 100%);
	flex-shrink: 0;
`;

// Override Modal.Content to support the side-by-side panel layout with internal scroll areas
const PanelContent = Styled(Modal.Content)`
	overflow: hidden;
	flex: 1;
	min-height: 0;
	padding: 0;
`;

// ---------------------------------------------------------------------------
// Session persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'forecast_ai_planning_session';

interface SessionData {
	threadId: string;
	proposal: ProjectProposal | null;
	phase: Phase;
	messages: ChatMessage[];
	toolStatuses: Record<string, boolean>;
}

function loadSession(): SessionData | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function saveSession(data: SessionData) {
	try {
		// Mark any streaming messages as done before persisting
		const safeMessages = data.messages.map(m => (m.isStreaming ? {...m, isStreaming: false} : m));
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify({...data, messages: safeMessages}));
	} catch {
		// storage full or unavailable — non-fatal
	}
}

function clearSession() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const AiProjectPlanningModal: React.FC<AiProjectPlanningModalProps> = ({closeModal, personId}) => {
	const history = useHistory();
	const savedSession = loadSession();

	const [phase, setPhase] = useState<Phase>(savedSession?.phase ?? 'idle');
	const [messages, setMessages] = useState<ChatMessage[]>(savedSession?.messages ?? []);
	const [proposal, setProposal] = useState<ProjectProposal | null>(savedSession?.proposal ?? null);
	const [warnings, setWarnings] = useState<Warning[]>([]);
	const [toolStatuses, setToolStatuses] = useState<Record<string, boolean>>(savedSession?.toolStatuses ?? {});
	const [resolvedApiKey, setResolvedApiKey] = useState('');

	useEffect(() => {
		fetchQuery(environment.getInstance(), apiKeyQuery, {})
			.toPromise()
			.then((data: AiProjectPlanningModal_ApiKey_Query$data | undefined) => {
				const key = (data?.viewer?.company?.apiKeys?.edges ?? []).find(e => e?.node?.active)?.node?.key ?? '';
				setResolvedApiKey(key);
			})
			.catch(() => undefined);
	}, []);

	// map from toolCallId (from SSE) -> message id in chat
	const toolCallIdToMsgId = useRef<Record<string, string>>({});
	// map from tool name -> message id — collapses repeated calls into one row
	const toolNameToMsgId = useRef<Record<string, string>>({});
	// active (in-flight) call count per grouped message id
	const activeToolCalls = useRef<Record<string, number>>({});
	const threadIdRef = useRef<string>(savedSession?.threadId ?? '');
	const proposalRef = useRef<ProjectProposal | null>(savedSession?.proposal ?? null);
	const createdProjectIdRef = useRef<number | null>(null);
	const createdProjectRelayIdRef = useRef<string>('');
	const abortRef = useRef<AbortController | null>(null);
	const refiningChipIdRef = useRef<string | null>(null);

	// Abort any in-flight stream on unmount
	useEffect(() => {
		return () => {
			abortRef.current?.abort();
		};
	}, []);

	// Persist full session on every relevant state change
	useEffect(() => {
		if (threadIdRef.current || messages.length > 0) {
			saveSession({threadId: threadIdRef.current, proposal, phase, messages, toolStatuses});
		}
	}, [messages, proposal, phase, toolStatuses]);

	// Keep proposalRef in sync so the approve handler can read the latest proposal
	useEffect(() => {
		proposalRef.current = proposal;
	}, [proposal]);

	const addMessage = useCallback((msg: ChatMessage) => {
		setMessages(prev => [...prev, msg]);
	}, []);

	const updateMessage = useCallback((id: string, updater: (m: ChatMessage) => ChatMessage) => {
		setMessages(prev => prev.map(m => (m.id === id ? updater(m) : m)));
	}, []);

	const buildCallbacks = useCallback(
		(): StreamCallbacks => ({
			onNewAssistantMessage: (msgId: string) => {
				addMessage({id: msgId, role: 'assistant', content: '', isStreaming: true});
			},
			onText: (msgId: string, delta: string) => {
				updateMessage(msgId, m => ({...m, content: m.content + delta}));
			},
			onMessageStreamEnd: (msgId: string) => {
				if (msgId) updateMessage(msgId, m => ({...m, isStreaming: false}));
			},
			onProposal: (p: ProjectProposal) => {
				setProposal(p);
				setWarnings([]);
			},
			onWarnings: (w: Warning[]) => {
				setWarnings(w);
			},
			onRefiningStarted: () => {
				const refiningId = SimpleUUID();
				refiningChipIdRef.current = refiningId;
				setToolStatuses(prev => ({...prev, [refiningId]: false}));
				addMessage({id: refiningId, role: 'tool', content: '', toolName: '__refining__', isStreaming: true});
			},
			onCommitted: (projectId: number | null, relayProjectId?: string) => {
				createdProjectIdRef.current = projectId;
				createdProjectRelayIdRef.current = relayProjectId ?? '';
				setPhase('committed');
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
			},
			onFinished: () => {
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				if (refiningChipIdRef.current) {
					const chipId = refiningChipIdRef.current;
					refiningChipIdRef.current = null;
					setToolStatuses(prev => ({...prev, [chipId]: true}));
				}
				if (createdProjectIdRef.current) {
					const relayId = createdProjectRelayIdRef.current || `P${createdProjectIdRef.current}`;
					closeModal();
					history.push(`/project/${encodeURIComponent(relayId)}/scoping`);
				} else {
					setPhase(proposalRef.current ? 'awaiting_approval' : 'idle');
				}
			},
			onError: (message: string) => {
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				if (refiningChipIdRef.current) {
					const chipId = refiningChipIdRef.current;
					refiningChipIdRef.current = null;
					setToolStatuses(prev => ({...prev, [chipId]: true}));
				}
				addMessage({id: SimpleUUID(), role: 'assistant', content: `Error: ${message}`});
				setPhase('error');
			},
			onToolCall: (toolCallId: string, toolName: string) => {
				const existingMsgId = toolNameToMsgId.current[toolName];
				if (existingMsgId) {
					toolCallIdToMsgId.current[toolCallId] = existingMsgId;
					activeToolCalls.current[existingMsgId] = (activeToolCalls.current[existingMsgId] ?? 1) + 1;
					setToolStatuses(prev => ({...prev, [existingMsgId]: false}));
					updateMessage(existingMsgId, m => ({
						...m,
						callCount: activeToolCalls.current[existingMsgId],
						isStreaming: true,
					}));
				} else {
					const msgId = SimpleUUID();
					toolCallIdToMsgId.current[toolCallId] = msgId;
					toolNameToMsgId.current[toolName] = msgId;
					activeToolCalls.current[msgId] = 1;
					setToolStatuses(prev => ({...prev, [msgId]: false}));
					addMessage({id: msgId, role: 'tool', content: '', toolName, callCount: 1, isStreaming: true});
				}
			},
			onToolResult: (toolCallId: string) => {
				const msgId = toolCallIdToMsgId.current[toolCallId];
				if (msgId) {
					const remaining = (activeToolCalls.current[msgId] ?? 1) - 1;
					activeToolCalls.current[msgId] = Math.max(0, remaining);
					if (remaining <= 0) {
						setToolStatuses(prev => ({...prev, [msgId]: true}));
						updateMessage(msgId, m => ({...m, isStreaming: false}));
					}
				}
			},
		}),
		[addMessage, updateMessage, closeModal, history]
	);

	const handleSend = useCallback(
		async (text: string, attachment: AttachedFile | null) => {
			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;

			setPhase('running');
			setWarnings([]);

			if (phase === 'awaiting_approval') {
				addMessage({
					id: SimpleUUID(),
					role: 'user',
					content: attachment ? `📄 ${attachment.filename}${text ? '\n' + text : ''}` : text,
				});
				try {
					await resumeAgent(
						threadIdRef.current,
						false,
						resolvedApiKey,
						buildCallbacks(),
						controller.signal,
						text,
						personId
					);
				} catch (err: unknown) {
					if (err instanceof DOMException && err.name === 'AbortError') {
						setPhase('awaiting_approval');
						return;
					}
					addMessage({
						id: SimpleUUID(),
						role: 'assistant',
						content: `Error: ${err instanceof Error ? err.message : 'Connection failed'}`,
					});
					setPhase('error');
				}
				return;
			}

			// Initial send — reuse the existing thread if one is active so the agent retains
			// conversation context. Only create a new thread on a genuine fresh start.
			toolCallIdToMsgId.current = {};
			toolNameToMsgId.current = {};
			activeToolCalls.current = {};
			createdProjectIdRef.current = null;
			refiningChipIdRef.current = null;
			setToolStatuses({});
			if (!threadIdRef.current) {
				threadIdRef.current = SimpleUUID();
			}
			const threadId = threadIdRef.current;
			setProposal(null);
			const newUserMsg: ChatMessage = {
				id: SimpleUUID(),
				role: 'user',
				content: attachment ? `📄 ${attachment.filename}${text ? '\n' + text : ''}` : text,
			};
			setMessages(prev =>
				prev.length === 0
					? [newUserMsg]
					: [...prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)), newUserMsg]
			);

			try {
				await invokeAgent(
					text || `Please extract the project brief from the attached document: ${attachment?.filename}`,
					threadId,
					resolvedApiKey,
					buildCallbacks(),
					controller.signal,
					attachment ?? undefined,
					personId
				);
			} catch (err: unknown) {
				if (err instanceof DOMException && err.name === 'AbortError') {
					setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
					setPhase(proposalRef.current ? 'awaiting_approval' : 'idle');
					return;
				}
				addMessage({
					id: SimpleUUID(),
					role: 'assistant',
					content: `Error: ${err instanceof Error ? err.message : 'Connection failed'}`,
				});
				setPhase('error');
			}
		},
		[phase, resolvedApiKey, personId, addMessage, updateMessage, buildCallbacks]
	);

	const handleApprove = useCallback(async () => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		setPhase('committing');
		try {
			await resumeAgent(
				threadIdRef.current,
				true,
				resolvedApiKey,
				buildCallbacks(),
				controller.signal,
				undefined,
				personId,
				proposalRef.current
			);
		} catch (err: unknown) {
			if (err instanceof DOMException && err.name === 'AbortError') {
				setPhase('awaiting_approval');
				return;
			}
			// Fall through — check if the project was still committed before the error
		}

		// onFinished already handled the success case — only handle failure here
		if (createdProjectIdRef.current) return;

		setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
		addMessage({id: SimpleUUID(), role: 'assistant', content: 'Project creation failed. Click Cancel to close.'});
		setPhase('error');
	}, [resolvedApiKey, personId, addMessage, buildCallbacks]);

	const handleReset = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
		clearSession();
		setPhase('idle');
		setMessages([]);
		setProposal(null);
		setWarnings([]);
		toolCallIdToMsgId.current = {};
		toolNameToMsgId.current = {};
		activeToolCalls.current = {};
		createdProjectIdRef.current = null;
		createdProjectRelayIdRef.current = '';
		refiningChipIdRef.current = null;
		setToolStatuses({});
		threadIdRef.current = '';
	}, []);

	const isRunningOrCommitting = phase === 'running' || phase === 'committing';

	return (
		<Modal onCloseModal={closeModal} size={Modal.SIZE.EXTRA_LARGE} setHeight={true}>
			<Modal.Title>
				<FlexRow gap="xs" alignItems="center">
					<TitleIconBadge>
						<Icon icon="sparkle" size="m" color="white" />
					</TitleIconBadge>
					AI Project Planning
				</FlexRow>
			</Modal.Title>
			<PanelContent>
				<Body>
					<ChatPanel
						messages={messages}
						phase={phase}
						onSend={handleSend}
						onStop={() => abortRef.current?.abort()}
						toolStatuses={toolStatuses}
					/>
					<Divider />
					<ProposalPanel proposal={proposal} warnings={warnings} onProposalChange={setProposal} />
				</Body>
			</PanelContent>
			<Modal.Footer>
				<Modal.TertiaryFooterButton disabled={isRunningOrCommitting} onPress={closeModal}>
					Cancel
				</Modal.TertiaryFooterButton>
				<Modal.SecondaryFooterButton onPress={handleReset}>Replan</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton disabled={phase !== 'awaiting_approval'} onPress={handleApprove}>
					Save Project ✓
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default AiProjectPlanningModal;
